import React from 'react';
import { ArrowRight, ExternalLink, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function LessonsOfLeadership() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const en = {
    back: 'Writing',
    badge: 'Five articles',
    title: 'Lessons of Leadership',
    intro: `A researcher once asked a simple question to people of different ages: what makes a good leader?

A five-year-old said: someone who lets you go first.

A teenager said: someone who really knows their stuff and knows how to get people to do their best - with decent rewards for the most productive.

An adult said: a good leader understands the whole system. She knows all of the people in an organisation are part of that system. The best way to keep it healthy is to get everyone on the same page.

All three are describing leadership. None of them are wrong. They are simply at different stages of the same development - a development that doesn't end, and that most people don't know they're on.

The moment most people step into a leadership role, they discover something nobody warned them about. The skills that made them excellent - the expertise, the reliability, the ability to get things done - are not quite the skills that make them effective as leaders. They still matter. But they are not enough.

This is not a failure. It is a transition. And it is the transition that almost nobody prepares you for.

Most of what we call leadership development doesn't address it either. It gives you frameworks, communication models, feedback tools, leadership styles. All useful. None of them quite answers the question that sits underneath: who do I need to become?

That question doesn't have a quick answer. But it has a direction.

Leadership and management are often treated as separate identities - different people, different roles, different levels of an organisation. Leaders inspire. Managers administrate. The implicit hierarchy is obvious. This framing is both wrong and unhelpful. The same person leads in some situations and manages in others - often within the same conversation. The question is not which one you are. It is which one the situation requires.

One thing worth being honest about: the frameworks and ideas in this series were tested against a specific version of the leadership challenge - the world of 2019, before the current wave of AI, before what is coming next had fully arrived. If I were building this series from scratch today, I would be asking different questions alongside the ones I asked then. Not discarding what I found - most of it holds up - but adding: what does leadership require when the environment is changing faster than any model can keep up with? What is still genuinely useful from everything that got us here, what needs to evolve, and what are the situations we are now facing for which there is no established model yet?

Those questions run underneath every article in this series. They do not have clean answers. But they are the right questions to be asking.`,
    articles: [{
      n: '01',
      title: 'The Head-Fake',
      desc: 'Leadership looks like it is about the leader. It is not. The head-fake is discovering what it is actually about - and why the expertise that got you here is not what will get you further.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '02',
      title: 'Leading and Managing',
      desc: 'The same person leads in some situations and manages in others. What kinds of situations call for which - and what happens when you apply the wrong mode to the wrong situation.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '03',
      title: 'Knowing Yourself',
      desc: 'You cannot lead others well without leading yourself first. What self-leadership actually means, what gets in the way, and why the space between stimulus and response is where the development happens.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '04',
      title: 'Developing Yourself',
      desc: 'The four selves of a leader - aware, descriptive, curious, appreciative - as a practical framework for building interpersonal clarity and coaching leadership.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '05',
      title: 'Communication and Followership',
      desc: 'Leadership is measured by followership behaviour, not leadership behaviour. What that means for how you communicate, how you tell stories, and what you are actually building.',
      href: 'https://helgig.substack.com',
      live: false
    }],
    substackCta: 'Follow on Substack - series publishing from autumn 2026',
    relatedTitle: 'Related',
    related: [{
      label: 'First-Time Manager course',
      href: '/services/courses/first-time-manager'
    }, {
      label: 'Coaching Skills for Managers',
      href: '/services/workshops/coaching-skills'
    }]
  };
  const is = {
    back: 'Skrif',
    badge: 'Fimm greinar',
    title: 'Lærdómar af forystu',
    intro: `Rannsakandi spurði einu sinni fólk á mismunandi aldri einfaldrar spurningar: Hvað gerir góðan leiðtoga?

Fimm ára barn svaraði: Einhver sem lætur þig fara fyrst.

Unglingur sagði: Einhver sem kann sitt fag virkilega vel og veit hvernig á að fá fólk til að leggja sig fram - með góðri umbun fyrir þá sem skila mestu.

Fullorðinn sagði: Góður leiðtogi skilur allt kerfið. Hún veit að allt fólkið í skipulagsheildini er hluti af því kerfi og að besta leiðin til að halda því heilbrigðu er að fá alla á sömu blaðsíðu.

Öll þrjú eru að lýsa forystu og ekkert þeirra hefur rangt fyrir sér. Þau standa einfaldlega á ólíkum tröppum á sömu þroska braut - ferlis sem aldrei lýkur, og sem flestir vita ekki einu sinni að þeir eru á.

Þegar fólk tekur fyrst að sér leiðtogahlutverk komast margir að því sem nánast enginn varaði þau við: þeir hæfileikar sem gerðu þig framurskarandi - sérþekkingin, áreiðanleikinn, getan til að koma hlutum í verk - eru ekki sömu hæfileikar og gera þig árangursríkan sem leiðtoga. Þeir skipta enn máli, en þeir duga ekki einir og sér. Þetta eru ekki mistök; þetta eru umskipti - og umskipti sem enginn undirbýr þig fullkomlega fyrir.

Flest það sem kallast leiðtogaþróun nær ekki alveg utan um þetta. Hún gefur þér verkfæri og ramma: samskiptalíkön, endurgjafartækni og „leiðtogastíla". Allt gagnlegt - en ekkert sem svarar dýpri spurningunni: Hver þarf ég nú að verða?

Það er ekki spurning með auðvelt eða hratt svar, en hún opnar á áframhaldandi vegferð. Slóða sem leiðir þig áfram inn í þessa dýpri áskorun, að þróa með þér áhrifaríka getu til að leiða aðra.

Oft er forysta og stjórnun meðhöndluð sem tvö aðskilin hlutverk: leiðtoginn og stjórnandinn, með sitt hvort hlutverkið og á ólíkum tröppum virðingarstigans. Leiðtogar veita innblástur, stjórnendur stjórna rekstri. Óbeint trúa margir því að leiðtogar séu „æðri". En veruleikinn er að í raun leiða og stjórna sömu einstaklingar - jafnvel innan sama samtals. Spurningin er ekki hvort þú sér leiðtogi eða stjórnandi, heldur hvað aðstæðurnar krefjast hverju sinni og hvernig þú mætir til leiks.

Lykilhugmyndir, verkfæri og rammar sem eru skoðuð í þessari seríu mótuðust út frá ákveðinni mynd af áskorun leiðtoga: hvernig leiðtogar gætu tileinkuð sér agile-forystuhæfni. Sjónarhornið var árið 2019–2020, áður en við gengum í gegnum COVID-tímabilið og áður en núrverandi bylgja gervigreindar skall á.

Það sem ég leyfi mér að skoða og leika mér að í þessari seríu er ákveðið viðhorf - ef ég byggði þetta námskeið og þessa greina-seríu frá grunni í dag, hvaða spurninga myndi ég spyrja samhliða þeim sem ég notaði þá?

Hvað gerir leiðtogann nauðsynlegan þegar umhverfið breytist hraðar en nokkurt líkan nær að fylgja? Hvað af því sem kom okkur hingað er enn gagnlegt? Hvaðar hugmyndir og nálganir eru að verða, eða eru orðnar, úreldar? Og hvaðar nýju aðstæðum stöndum við frammi fyrir þar sem við höfum ekki góð eða sannreynd líkön til að takast á við þær?

Þassar spurningar liggja undir hverri grein í seríunni. Það hefur lengi verið mín sannfæring að mikilvægara sé að spyrja réttu spurninganna heldur en að þykjast vita öll svörin. Ég held að allir sem skara fram úr í að leiða skilji það líka.`,
    articles: [{
      n: '01',
      title: 'Hinn duldi vendipunktur',
      desc: 'Forysta lítur út fyrir að snúa um leiðtogann. Það gerir hún ekki. Vendipunkturinn er þegar við uppgvatum hvað hún snýst raunverulega um - og hvers vegna sérþekkingin sem kom þer hingað duga ekki ein og sér.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '02',
      title: 'Að leiða og stjórna',
      desc: 'Sömi maðurinn leiðir í sumum aðstæðum og stjórnar í öðrum. Hvað kallar á hvað - og hvað gerist þegar við beitum rangri nálgun á röngar aðstæður.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '03',
      title: 'Að þekka sjalf/sjálfan sig',
      desc: 'Þú getur ekki leið aðra vel áður en þú leiðir þig sjalf/sjálfan vel. Hvað sjálfsleiðtógumót þeýðir raunverulega - og hvers vegna rúnið milli áreitar og viðbragðs er þegar þroðunin gerðist.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '04',
      title: 'Að þroða sjalf/sjálfan sig',
      desc: 'Fjórar sjálf leiðtogans - meðvitaður, lýsandi, forvítinn, þakklægill - sem gagnlegt rämmur til að skapa meðvitnað og Coaching leiðtógumót.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '05',
      title: 'Samskipti og fylgið',
      desc: 'Forysta er mæld í hegðun þeirra sem velja að fylgja - ekki hegðun leiðtogans. Hvað það þeýðir fyrir hó þú talar, hvernig þú segir frá, og hvað þú ert raunverulega að byggja.',
      href: 'https://helgig.substack.com',
      live: false
    }],
    substackCta: 'Fylgjast á Substack - greinaröðin kemur haust 2026',
    relatedTitle: 'Tengt',
    related: [{
      label: 'Stjórnandi í fyrsta sinn',
      href: '/services/courses/first-time-manager'
    }, {
      label: 'Coaching Skills for Managers',
      href: '/services/workshops/coaching-skills'
    }]
  };
  const c = isIS ? is : en;
  return <PageTransition data-id="element-2139">
      <div className="pt-12 pb-24" data-id="element-2140">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2141">
          <Animate data-id="element-2142">
            <Link to="/writing" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal mb-8 transition-colors" data-id="element-2143">
              <ChevronRight size={14} className="rotate-180" data-id="element-2144" />
              {c.back}
            </Link>
          </Animate>
          <Animate data-id="element-2145">
            <span className="inline-block px-3 py-1 bg-teal/10 text-teal font-medium rounded-full text-xs mb-4" data-id="element-2146">
              {c.badge}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6" data-id="element-2147">
              {c.title}
            </h1>
            <div className="prose prose-lg text-gray-500 leading-relaxed mb-12 space-y-4" data-id="element-2148">
              {c.intro.split('\n\n').map((para, i) => <p key={i} data-id="element-2149">{para}</p>)}
            </div>
          </Animate>
          <div className="space-y-4 mb-12" data-id="element-2150">
            {c.articles.map((a, i) => <Animate key={a.n} delay={i * 0.05} data-id="element-2151">
                <Card className="p-6 border border-gray-100 opacity-70" data-id="element-2152">
                  <div className="flex items-start gap-4" data-id="element-2153">
                    <span className="text-2xl font-bold text-gray-200 font-mono flex-shrink-0 leading-none mt-1" data-id="element-2154">
                      {a.n}
                    </span>
                    <div className="flex-1" data-id="element-2155">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2156">
                        <h2 className="font-bold text-charcoal" data-id="element-2157">{a.title}</h2>
                        <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-medium" data-id="element-2158">
                          {isIS ? 'Kemur fljótlega' : 'Coming soon'}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2159">
                        {a.desc}
                      </p>
                    </div>
                  </div>
                </Card>
              </Animate>)}
          </div>
          <Animate data-id="element-2160">
            <Card className="p-6 bg-teal/5 border-teal/20 border text-center mb-12" data-id="element-2161">
              <p className="text-gray-600 mb-4" data-id="element-2162">{c.substackCta}</p>
              <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" data-id="element-2163">
                <Button variant="outline" rightIcon={<ExternalLink size={16} data-id="element-2165" />} data-id="element-2164">
                  Substack
                </Button>
              </a>
            </Card>
          </Animate>
          <Animate data-id="element-2166">
            <div data-id="element-2167">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2168">
                {c.relatedTitle}
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-2169">
                {c.related.map(r => <Link key={r.href} to={r.href} data-id="element-2170">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-2172" />} data-id="element-2171">
                      {r.label}
                    </Button>
                  </Link>)}
              </div>
            </div>
          </Animate>
        </div>
      </div>
    </PageTransition>;
}